/**
 * Approved Sources & External Providers Section Component
 * POV-0101: Section 5 of Part Overview Panel
 *
 * Per PRD Section 7.5 + Completion Prompt Phase A:
 * - Approved sources table with status badges
 * - Source detail panel with org metadata
 * - Add/Edit/Remove modals (ALL controlled — reason required)
 * - Status lifecycle: Approved → Conditional → Suspended → Disqualified
 * - AS9100D Guidance banner (Clauses 8.4.1, 8.4.2, 8.4.3)
 *
 * section_key: "approved_sources_external_providers"
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2,
  Plus,
  Lock,
  Loader2,
  AlertTriangle,
  Search,
  Info,
  Pencil,
  Trash2,
  ShieldCheck,
  ShieldAlert,
  ShieldClose,
  ShieldOff,
  Clock,
  CalendarDays,
  Filter,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ViewRelatedHistoryLink } from './HistorySection';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { toast } from 'sonner';
import api from '../../services/api';

const SECTION_KEY = 'approved_sources_external_providers';

const SOURCE_TYPE_LABELS = {
  Supplier: 'Supplier',
  Manufacturer: 'Manufacturer',
  SpecialProcessor: 'Special Processor',
};

const STATUS_CONFIG = {
  Approved: { label: 'Approved', className: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: ShieldCheck },
  Conditional: { label: 'Conditional', className: 'bg-amber-100 text-amber-700 border-amber-200', icon: ShieldAlert },
  Suspended: { label: 'Suspended', className: 'bg-red-100 text-red-700 border-red-200', icon: ShieldClose },
  Disqualified: { label: 'Disqualified', className: 'bg-slate-200 text-slate-600 border-slate-300', icon: ShieldOff },
};

/** Status Badge */
const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status];
  if (!config) return <span className="text-xs text-slate-400">{status || '—'}</span>;
  const IconComp = config.icon;
  return (
    <span
      className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border', config.className)}
      data-testid={`status-badge-${status}`}
    >
      <IconComp className="w-3 h-3" />
      {config.label}
    </span>
  );
};

/** AS9100D Guidance Banner */
const GuidanceBanner = () => (
  <div className="mb-4 p-3 bg-sky-50 border border-sky-200 rounded-lg text-xs" data-testid="guidance-banner-approved-sources">
    <div className="flex items-start gap-2">
      <Info className="w-3.5 h-3.5 text-sky-600 mt-0.5 shrink-0" />
      <div>
        <p className="font-semibold text-sky-800">AS9100D Clauses: 8.4.1, 8.4.2, 8.4.3</p>
        <ul className="mt-1 space-y-0.5 text-sky-700">
          <li>Maintain approved supplier list with qualification status</li>
          <li>Track approval scope and effective dates</li>
          <li>Audit supplier changes with reason-for-change</li>
          <li>Support conditional approvals and suspensions</li>
        </ul>
      </div>
    </div>
  </div>
);

const EXPIRY_STATUS_CONFIG = {
  expired: { label: 'Expired', className: 'bg-red-100 text-red-800 border-red-200', icon: AlertTriangle },
  expiring_30d: { label: '< 30 days', className: 'bg-orange-100 text-orange-800 border-orange-200', icon: Clock },
  expiring_6m: { label: '< 6 months', className: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  expiring_1y: { label: '< 1 year', className: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: CalendarDays },
  active: { label: 'Active', className: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: null },
  no_expiry: { label: 'No Expiry', className: 'bg-slate-50 text-slate-500 border-slate-200', icon: null },
};

const EXPIRY_FILTER_OPTIONS = [
  { value: 'all', label: 'All Sources' },
  { value: 'expired', label: 'Qualification Expired' },
  { value: 'expiring_30d', label: 'Expiring in 30 days' },
  { value: 'expiring_6m', label: 'Expiring in 6 months' },
  { value: 'expiring_1y', label: 'Expiring in 1 year' },
  { value: 'expiring_any', label: 'Any Expiry Warning' },
];

/** Qualification Expiry Badge */
const ExpiryBadge = ({ status, days }) => {
  const config = EXPIRY_STATUS_CONFIG[status];
  if (!config || status === 'active' || status === 'no_expiry') return null;
  const IconComp = config.icon;
  return (
    <span className={cn('inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium border', config.className)} data-testid={`expiry-badge-${status}`}>
      {IconComp && <IconComp className="w-2.5 h-2.5" />}
      {status === 'expired' ? 'Expired' : `${Math.abs(days)}d`}
    </span>
  );
};

/** Qualification Expiry Summary Banner */
const ExpirySummary = ({ sources }) => {
  const expired = sources.filter((s) => s.expiry_status === 'expired').length;
  const exp30 = sources.filter((s) => s.expiry_status === 'expiring_30d').length;
  const exp6m = sources.filter((s) => s.expiry_status === 'expiring_6m').length;
  if (expired === 0 && exp30 === 0 && exp6m === 0) return null;

  return (
    <div className="flex flex-wrap gap-2" data-testid="expiry-summary">
      {expired > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 px-2.5 py-1.5 rounded-md">
          <AlertTriangle className="w-3.5 h-3.5" />
          <span className="font-semibold">{expired}</span> qualification{expired > 1 ? 's' : ''} expired — re-qualification required
        </div>
      )}
      {exp30 > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-orange-700 bg-orange-50 border border-orange-200 px-2.5 py-1.5 rounded-md">
          <Clock className="w-3.5 h-3.5" />
          <span className="font-semibold">{exp30}</span> expiring within 30 days
        </div>
      )}
      {exp6m > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1.5 rounded-md">
          <Clock className="w-3.5 h-3.5" />
          <span className="font-semibold">{exp6m}</span> expiring within 6 months
        </div>
      )}
    </div>
  );
};

/** Reason for Change dialog fields */
const ReasonFields = ({ reasonCode, setReasonCode, reasonText, setReasonText, reasonCodes }) => (
  <div className="space-y-3 pt-3 border-t">
    <div>
      <Label className="text-xs flex items-center gap-1">
        <Lock className="w-3 h-3 text-amber-600" /> Reason for Change *
      </Label>
      <Select value={reasonCode} onValueChange={setReasonCode}>
        <SelectTrigger data-testid="reason-code-select"><SelectValue placeholder="Select reason..." /></SelectTrigger>
        <SelectContent>
          {(reasonCodes || []).map((rc) => (
            <SelectItem key={rc.code} value={rc.code}>{rc.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
    {reasonCode === 'other' && (
      <div>
        <Label className="text-xs">Reason Text *</Label>
        <Textarea value={reasonText} onChange={(e) => setReasonText(e.target.value)} placeholder="Describe reason..." rows={2} data-testid="reason-text-input" />
      </div>
    )}
  </div>
);

/** Add Approved Source Modal */
const AddSourceModal = ({ open, onClose, partId, onAdded, reasonCodes }) => {
  const [step, setStep] = useState('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [sourceType, setSourceType] = useState('');
  const [approvalStatus, setApprovalStatus] = useState('Approved');
  const [scope, setScope] = useState('');
  const [qualificationRef, setQualificationRef] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [saving, setSaving] = useState(false);
  // Create new org
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgTypes, setNewOrgTypes] = useState([]);
  const [newExternalDuns, setNewExternalDuns] = useState('');
  const [newExternalCage, setNewExternalCage] = useState('');

  const searchOrgs = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await api.get(`/api/v1/approved-sources/organizations/search?q=${encodeURIComponent(searchQuery)}&limit=20`);
      setSearchResults(res.data);
    } catch { toast.error('Failed to search organizations'); }
    finally { setSearching(false); }
  }, [searchQuery]);

  const handleSubmit = async () => {
    if (!sourceType || !reasonCode) { toast.error('Source type and reason are required'); return; }
    if (reasonCode === 'other' && !reasonText.trim()) { toast.error('Please provide reason text'); return; }

    let orgId = selectedOrg?.org_id;

    // Create org first if in create mode
    if (step === 'create') {
      if (!newOrgName.trim()) { toast.error('Organization name is required'); return; }
      try {
        const orgTypes = newOrgTypes.length > 0 ? newOrgTypes : [sourceType];
        const external_ids = {};
        if (newExternalDuns.trim()) external_ids.DUNS = newExternalDuns;
        if (newExternalCage.trim()) external_ids.CAGE = newExternalCage;
        const orgRes = await api.post('/api/v1/approved-sources/organizations', {
          org_name: newOrgName,
          org_types: orgTypes,
          external_ids: Object.keys(external_ids).length > 0 ? external_ids : null,
          status: 'Active',
        });
        orgId = orgRes.data.org_id;
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to create organization');
        return;
      }
    }

    if (!orgId) { toast.error('Select or create an organization'); return; }

    setSaving(true);
    try {
      const res = await api.post(`/api/v1/approved-sources/part/${partId}`, {
        source_org_id: orgId,
        source_type: sourceType,
        approval_status: approvalStatus,
        scope: scope || null,
        qualification_ref: qualificationRef || null,
        reason_code: reasonCode,
        reason_text: reasonText || null,
      });
      toast.success(`Added ${selectedOrg?.org_name || newOrgName} as approved ${SOURCE_TYPE_LABELS[sourceType]}`);
      onAdded(res.data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add approved source'); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    if (!open) {
      setStep('search'); setSearchQuery(''); setSearchResults([]); setSelectedOrg(null);
      setSourceType(''); setApprovalStatus('Approved'); setScope(''); setQualificationRef('');
      setReasonCode(''); setReasonText('');
      setNewOrgName(''); setNewOrgTypes([]); setNewExternalDuns(''); setNewExternalCage('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="add-source-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Building2 className="w-4 h-4" /> Add Approved Source</DialogTitle>
          <DialogDescription>Add an approved supplier, manufacturer, or special processor. This is a controlled action.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 bg-slate-100 p-1 rounded-md">
          <button onClick={() => setStep('search')} className={cn('flex-1 text-xs py-1.5 px-3 rounded', step === 'search' ? 'bg-white shadow text-slate-900' : 'text-slate-500')} data-testid="tab-search-org">Search Existing</button>
          <button onClick={() => setStep('create')} className={cn('flex-1 text-xs py-1.5 px-3 rounded', step === 'create' ? 'bg-white shadow text-slate-900' : 'text-slate-500')} data-testid="tab-create-org">Create New</button>
        </div>

        {step === 'search' ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input placeholder="Search organizations..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchOrgs()} data-testid="org-search-input" />
              <Button size="sm" variant="outline" onClick={searchOrgs} disabled={searching}>
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
            {searchResults.length > 0 && (
              <div className="max-h-40 overflow-y-auto border rounded-md divide-y">
                {searchResults.map((org) => (
                  <button
                    key={org.org_id}
                    onClick={() => setSelectedOrg(org)}
                    className={cn('w-full text-left p-2 text-xs hover:bg-slate-50 transition', selectedOrg?.org_id === org.org_id && 'bg-sky-50 border-l-2 border-sky-500')}
                    data-testid={`org-result-${org.org_id}`}
                  >
                    <span className="font-semibold">{org.org_name}</span>
                    <span className="text-slate-500 ml-2">{(org.org_types || []).join(', ')}</span>
                    {org.external_ids?.CAGE && <span className="text-slate-400 ml-2 font-mono">CAGE: {org.external_ids.CAGE}</span>}
                  </button>
                ))}
              </div>
            )}
            {selectedOrg && (
              <div className="p-2 bg-sky-50 rounded text-xs">Selected: <span className="font-semibold">{selectedOrg.org_name}</span></div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div><Label className="text-xs">Organization Name *</Label><Input value={newOrgName} onChange={(e) => setNewOrgName(e.target.value)} placeholder="Acme Industries" data-testid="new-org-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">DUNS Number</Label><Input value={newExternalDuns} onChange={(e) => setNewExternalDuns(e.target.value)} placeholder="123456789" /></div>
              <div><Label className="text-xs">CAGE Code</Label><Input value={newExternalCage} onChange={(e) => setNewExternalCage(e.target.value)} placeholder="1ABC2" /></div>
            </div>
          </div>
        )}

        {/* Source Configuration */}
        <div className="space-y-3 pt-2 border-t">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Source Type *</Label>
              <Select value={sourceType} onValueChange={setSourceType}>
                <SelectTrigger data-testid="source-type-select"><SelectValue placeholder="Select type..." /></SelectTrigger>
                <SelectContent>
                  {Object.entries(SOURCE_TYPE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Initial Status</Label>
              <Select value={approvalStatus} onValueChange={setApprovalStatus}>
                <SelectTrigger data-testid="approval-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Approved">Approved</SelectItem>
                  <SelectItem value="Conditional">Conditional</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Scope of Approval</Label>
            <Input value={scope} onChange={(e) => setScope(e.target.value)} placeholder="e.g., Supply of PCB assemblies per IPC-A-610" data-testid="scope-input" />
          </div>
          <div>
            <Label className="text-xs">Qualification Evidence Ref</Label>
            <Input value={qualificationRef} onChange={(e) => setQualificationRef(e.target.value)} placeholder="e.g., QR-2026-001" data-testid="qualification-ref-input" />
          </div>
        </div>

        <ReasonFields
          reasonCode={reasonCode}
          setReasonCode={setReasonCode}
          reasonText={reasonText}
          setReasonText={setReasonText}
          reasonCodes={reasonCodes}
        />

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={saving || !sourceType || !reasonCode || (step === 'search' && !selectedOrg)} data-testid="add-source-confirm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Add Source
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Edit / Update Status Modal */
const EditSourceModal = ({ open, onClose, source, partId, onUpdated, reasonCodes, statusTransitions }) => {
  const [approvalStatus, setApprovalStatus] = useState('');
  const [scope, setScope] = useState('');
  const [qualificationRef, setQualificationRef] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && source) {
      setApprovalStatus(source.approval_status || '');
      setScope(source.scope || '');
      setQualificationRef(source.qualification_ref || '');
      setReasonCode(''); setReasonText('');
    }
  }, [open, source]);

  const validTransitions = statusTransitions?.[source?.approval_status] || [];

  const handleSubmit = async () => {
    if (!reasonCode) { toast.error('Reason is required'); return; }
    if (reasonCode === 'other' && !reasonText.trim()) { toast.error('Please provide reason text'); return; }
    setSaving(true);
    try {
      const payload = { reason_code: reasonCode, reason_text: reasonText || null };
      if (approvalStatus !== source.approval_status) payload.approval_status = approvalStatus;
      if (scope !== (source.scope || '')) payload.scope = scope || null;
      if (qualificationRef !== (source.qualification_ref || '')) payload.qualification_ref = qualificationRef || null;
      const res = await api.put(`/api/v1/approved-sources/part/${partId}/${source.approved_source_id}`, payload);
      toast.success(`Updated ${source.org_name || 'source'}`);
      onUpdated(res.data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update source'); }
    finally { setSaving(false); }
  };

  if (!source) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="edit-source-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="w-4 h-4" /> Edit Approved Source</DialogTitle>
          <DialogDescription>Modify source details. All changes are controlled.</DialogDescription>
        </DialogHeader>

        <div className="text-xs p-2 bg-slate-50 rounded flex items-center gap-2">
          <Building2 className="w-3.5 h-3.5 text-slate-400" />
          <span className="font-semibold">{source.org_name}</span>
          <StatusBadge status={source.approval_status} />
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs flex items-center gap-1"><Lock className="w-3 h-3 text-amber-600" /> Approval Status</Label>
            <Select value={approvalStatus} onValueChange={setApprovalStatus}>
              <SelectTrigger data-testid="edit-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={source.approval_status}>{source.approval_status} (current)</SelectItem>
                {validTransitions.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {validTransitions.length === 0 && source.approval_status === 'Disqualified' && (
              <p className="text-[10px] text-red-500 mt-1">Disqualified is a terminal state — no transitions available</p>
            )}
          </div>
          <div>
            <Label className="text-xs">Scope of Approval</Label>
            <Input value={scope} onChange={(e) => setScope(e.target.value)} data-testid="edit-scope-input" />
          </div>
          <div>
            <Label className="text-xs">Qualification Evidence Ref</Label>
            <Input value={qualificationRef} onChange={(e) => setQualificationRef(e.target.value)} data-testid="edit-qualification-ref" />
          </div>
        </div>

        <ReasonFields
          reasonCode={reasonCode}
          setReasonCode={setReasonCode}
          reasonText={reasonText}
          setReasonText={setReasonText}
          reasonCodes={reasonCodes}
        />

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={saving || !reasonCode} data-testid="edit-source-confirm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Pencil className="w-4 h-4 mr-1" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Remove Source Modal (Controlled) */
const RemoveSourceModal = ({ open, onClose, source, partId, onRemoved, reasonCodes }) => {
  const [reasonCode, setReasonCode] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [saving, setSaving] = useState(false);

  const handleRemove = async () => {
    if (!reasonCode) { toast.error('Reason is required'); return; }
    if (reasonCode === 'other' && !reasonText.trim()) { toast.error('Please provide reason text'); return; }
    setSaving(true);
    try {
      await api.delete(`/api/v1/approved-sources/part/${partId}/${source.approved_source_id}`, {
        data: { reason_code: reasonCode, reason_text: reasonText || null },
      });
      toast.success(`Removed ${source.org_name || 'source'}`);
      onRemoved(source.approved_source_id);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove source'); }
    finally { setSaving(false); }
  };

  useEffect(() => { if (!open) { setReasonCode(''); setReasonText(''); } }, [open]);

  if (!source) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="remove-source-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700"><Trash2 className="w-4 h-4" /> Remove Approved Source</DialogTitle>
          <DialogDescription>This is a controlled action. You must provide a reason.</DialogDescription>
        </DialogHeader>
        <div className="text-xs p-2 bg-slate-50 rounded flex items-center gap-2">
          <Building2 className="w-3.5 h-3.5 text-slate-400" />
          <span className="font-semibold">{source.org_name}</span>
          <span className="text-slate-500">({SOURCE_TYPE_LABELS[source.source_type]})</span>
        </div>

        <ReasonFields
          reasonCode={reasonCode}
          setReasonCode={setReasonCode}
          reasonText={reasonText}
          setReasonText={setReasonText}
          reasonCodes={reasonCodes}
        />

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" size="sm" onClick={handleRemove} disabled={saving || !reasonCode} data-testid="remove-source-confirm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
            Remove
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Main Section Component */
const ApprovedSourcesSection = ({ partId, onViewRelatedHistory }) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [statusTransitions, setStatusTransitions] = useState({});
  const [expiryFilter, setExpiryFilter] = useState('all');

  const loadSources = useCallback(async () => {
    if (!partId) return;
    setLoading(true);
    setError(null);
    try {
      const [sourcesRes, cfRes] = await Promise.all([
        api.get(`/api/v1/approved-sources/part/${partId}`),
        api.get('/api/v1/approved-sources/controlled-fields'),
      ]);
      setSources(sourcesRes.data);
      setReasonCodes(cfRes.data.reason_codes || []);
      setStatusTransitions(cfRes.data.status_transitions || {});
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load approved sources');
    } finally { setLoading(false); }
  }, [partId]);

  useEffect(() => { loadSources(); }, [loadSources]);

  const handleAdded = (newSource) => setSources((prev) => [newSource, ...prev]);
  const handleUpdated = (updated) => {
    setSources((prev) => prev.map((s) => s.approved_source_id === updated.approved_source_id ? updated : s));
    if (selectedSource?.approved_source_id === updated.approved_source_id) setSelectedSource(updated);
  };
  const handleRemoved = (id) => {
    setSources((prev) => prev.filter((s) => s.approved_source_id !== id));
    if (selectedSource?.approved_source_id === id) setSelectedSource(null);
  };

  const suspendedCount = sources.filter((s) => s.approval_status === 'Suspended').length;
  const disqualifiedCount = sources.filter((s) => s.approval_status === 'Disqualified').length;

  if (loading) {
    return (<div className="space-y-3 animate-pulse"><div className="h-16 bg-slate-100 rounded" /><div className="h-32 bg-slate-100 rounded" /></div>);
  }

  if (error) {
    return (
      <div className="text-center py-6">
        <AlertTriangle className="w-6 h-6 text-red-400 mx-auto mb-2" />
        <p className="text-sm text-red-600">{error}</p>
        <Button variant="outline" size="sm" className="mt-2" onClick={loadSources}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="approved-sources-section">
      <GuidanceBanner />

      {/* Qualification Expiry Summary */}
      <ExpirySummary sources={sources} />

      {/* Status Warnings */}
      {suspendedCount > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 px-2.5 py-1.5 rounded-md" data-testid="suspended-warning">
          <ShieldClose className="w-3.5 h-3.5" />
          <span className="font-semibold">{suspendedCount}</span> source{suspendedCount > 1 ? 's' : ''} suspended
        </div>
      )}
      {disqualifiedCount > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-slate-600 bg-slate-100 border border-slate-200 px-2.5 py-1.5 rounded-md" data-testid="disqualified-warning">
          <ShieldOff className="w-3.5 h-3.5" />
          <span className="font-semibold">{disqualifiedCount}</span> source{disqualifiedCount > 1 ? 's' : ''} disqualified
        </div>
      )}

      {/* Header + Filter + Add action */}
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-slate-700 shrink-0">Approved Sources</h4>
        <div className="flex items-center gap-2">
          <Select value={expiryFilter} onValueChange={setExpiryFilter}>
            <SelectTrigger className="h-7 text-[10px] w-[160px]" data-testid="expiry-filter-select">
              <Filter className="w-3 h-3 mr-1 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXPIRY_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowAddModal(true)} data-testid="add-source-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Source
          </Button>
        </div>
      </div>

      {/* Sources Table (filtered) */}
      {(() => {
        const filteredSources = expiryFilter === 'all'
          ? sources
          : expiryFilter === 'expiring_any'
            ? sources.filter((s) => ['expired', 'expiring_30d', 'expiring_6m', 'expiring_1y'].includes(s.expiry_status))
            : sources.filter((s) => s.expiry_status === expiryFilter);

        return filteredSources.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm" data-testid="no-sources">
          <Building2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
          {expiryFilter !== 'all' ? 'No sources match the selected filter' : 'No approved sources for this part'}
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs">Organization</TableHead>
                <TableHead className="text-xs w-28">Type</TableHead>
                <TableHead className="text-xs w-28">Status</TableHead>
                <TableHead className="text-xs w-24">Qualification</TableHead>
                <TableHead className="text-xs w-16 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSources.map((source) => (
                <TableRow
                  key={source.approved_source_id}
                  onClick={() => setSelectedSource(source)}
                  className={cn('cursor-pointer text-xs transition-colors', selectedSource?.approved_source_id === source.approved_source_id ? 'bg-sky-50' : 'hover:bg-slate-50')}
                  data-testid={`source-row-${source.approved_source_id}`}
                >
                  <TableCell>
                    <span className="font-medium text-xs">{source.org_name || '—'}</span>
                    {source.external_ids?.CAGE && (
                      <span className="text-slate-400 ml-1.5 text-[10px] font-mono">CAGE: {source.external_ids.CAGE}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{SOURCE_TYPE_LABELS[source.source_type] || source.source_type}</TableCell>
                  <TableCell><StatusBadge status={source.approval_status} /></TableCell>
                  <TableCell>
                    {source.effective_to ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] text-slate-500 font-mono">{source.effective_to}</span>
                        <ExpiryBadge status={source.expiry_status} days={source.days_until_expiry} />
                      </div>
                    ) : (
                      <span className="text-[10px] text-slate-400">No expiry</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditTarget(source); }}
                        className="p-1 hover:bg-slate-100 rounded transition"
                        data-testid={`edit-source-${source.approved_source_id}`}
                      >
                        <Pencil className="w-3 h-3 text-slate-500" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setRemoveTarget(source); }}
                        className="p-1 hover:bg-red-50 rounded transition"
                        data-testid={`remove-source-${source.approved_source_id}`}
                      >
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      );
      })()}

      {/* Selected Source Detail */}
      {selectedSource && (
        <div className="border rounded-lg p-3 bg-slate-50 space-y-2" data-testid="source-detail-panel">
          <h5 className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5" /> Source Details
          </h5>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
            <div><span className="text-slate-500">Organization:</span> <span className="font-medium">{selectedSource.org_name || '—'}</span></div>
            <div><span className="text-slate-500">Source Type:</span> {SOURCE_TYPE_LABELS[selectedSource.source_type]}</div>
            <div>
              <span className="text-slate-500">Status:</span>{' '}
              <StatusBadge status={selectedSource.approval_status} />
            </div>
            <div><span className="text-slate-500">Scope:</span> {selectedSource.scope || '—'}</div>
            <div><span className="text-slate-500">Applies To:</span> {selectedSource.part_revision_id ? `Revision ${selectedSource.part_revision_id}` : 'All Revisions'}</div>
            <div><span className="text-slate-500">Qualification Ref:</span> {selectedSource.qualification_ref || '—'}</div>
            <div><span className="text-slate-500">Effective From:</span> {selectedSource.effective_from || '—'}</div>
            <div><span className="text-slate-500">Effective To:</span> {selectedSource.effective_to || '—'}</div>
            <div>
              <span className="text-slate-500">Qualification Expiry:</span>{' '}
              {selectedSource.expiry_status === 'no_expiry' ? <span className="text-slate-400">No expiry set</span> :
               selectedSource.expiry_status === 'expired' ? <span className="text-red-600 font-semibold">Expired ({Math.abs(selectedSource.days_until_expiry)} days ago) — Re-qualification required</span> :
               selectedSource.expiry_status === 'active' ? <span className="text-emerald-600">{selectedSource.days_until_expiry} days remaining</span> :
               <span className="text-amber-600 font-medium">{selectedSource.days_until_expiry} days remaining</span>}
            </div>
            {selectedSource.external_ids && (
              <>
                {selectedSource.external_ids.DUNS && <div><span className="text-slate-500">DUNS:</span> <span className="font-mono">{selectedSource.external_ids.DUNS}</span></div>}
                {selectedSource.external_ids.CAGE && <div><span className="text-slate-500">CAGE:</span> <span className="font-mono">{selectedSource.external_ids.CAGE}</span></div>}
              </>
            )}
          </div>
          <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
            <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => setEditTarget(selectedSource)} data-testid="detail-edit-btn">
              <Lock className="w-3 h-3 mr-1" /> Edit
            </Button>
            <Button size="sm" variant="outline" className="h-6 text-[10px] text-red-600 border-red-200 hover:bg-red-50" onClick={() => setRemoveTarget(selectedSource)} data-testid="detail-remove-btn">
              <Lock className="w-3 h-3 mr-1" /> Remove
            </Button>
          </div>
        </div>
      )}

      <ViewRelatedHistoryLink sectionKey={SECTION_KEY} onClick={onViewRelatedHistory} />

      <AddSourceModal open={showAddModal} onClose={() => setShowAddModal(false)} partId={partId} onAdded={handleAdded} reasonCodes={reasonCodes} />
      <EditSourceModal open={!!editTarget} onClose={() => setEditTarget(null)} source={editTarget} partId={partId} onUpdated={handleUpdated} reasonCodes={reasonCodes} statusTransitions={statusTransitions} />
      <RemoveSourceModal open={!!removeTarget} onClose={() => setRemoveTarget(null)} source={removeTarget} partId={partId} onRemoved={handleRemoved} reasonCodes={reasonCodes} />
    </div>
  );
};

export default ApprovedSourcesSection;
